import { assertUncomputedFromBase, fromBaseClone, fromBaseRecord, fromBaseUuid, FromBaseStageMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import { compileSurfacePictures, surfaceField, surfacePictureRows } from './from-base-encounter-media';
import { phpJsonInt, phpJsonTruthy } from './php-json-values';
import { compileEncounterShellFromBase } from './from-base-encounter-shell';
import type { EncounterShellSource, FromBaseEncounterShellMetadata } from './from-base-encounter-shell';
import { encounterField } from '../../shared/array-types';

export const FROM_BASE_LOCATION_REMAINING_STAGES = ['location-hierarchy-shells', 'location-database-hierarchy-order'] as const;
const columns = ['location_private', 'location_deleted', 'location_created_by_id', 'location_id', 'location_name',
    'location_description', 'location_gm_notes', 'location_master_parent_id', 'location_master_parent_name', 'location_master_parent_type',
    'location_parent_id', 'location_parent_name', 'location_type', 'location_world', 'location_zone_prefix', 'location_environment',
    'location_ecosystem', 'location_ecosystem_cycles', 'location_localized_phenomena', 'location_flora_and_fauna',
    'location_founding_date_year', 'location_founding_date_month', 'location_founding_date_day', 'location_founding_date_era',
    'location_population', 'location_inhabitants_demonym', 'location_demographics', 'location_government', 'location_infrastructure',
    'location_districts', 'location_assets', 'location_defenses', 'location_industry_and_trade', 'location_guilds_and_factions',
    'location_points_of_interest', 'location_geography', 'location_climate', 'location_natural_resources',
    'location_construction_date_year', 'location_construction_date_month', 'location_construction_date_day', 'location_construction_date_era',
    'location_date_that_it_became_a_ruin_year', 'location_date_that_it_became_a_ruin_month', 'location_date_that_it_became_a_ruin_day',
    'location_date_that_it_became_a_ruin_era', 'location_alternative_names', 'location_purpose', 'location_alterations',
    'location_architecture', 'location_history', 'location_tourism', 'location_type_name', 'count_attached_encounters'];
export const LOCATION_SHELL_CONTEXT_FIELDS = ['location_master_parent_name', 'location_master_parent_type',
    'location_parent_name', 'location_type_name', 'count_attached_encounters'] as const;
export interface FromBaseLocation {
    entity_id: string;
    base_rev: number;
    locations: Row;
    pictures?: readonly Row[];
    media_context?: readonly Row[];
    shell_context?: Row;
    location_map_markers: readonly Row[];
    /** SELECT-only ids in native joined marker order; preserves SQL collation and ties. */
    marker_order?: readonly string[];
    /** Depth-one SELECT closure; presentation nesting stays outside the shared surface row. */
    hierarchy_context?: FromBaseLocationHierarchy;
    catalog?: Readonly<Record<string, Row>>;
}
export interface FromBaseLocationHierarchy {
    locations: readonly FromBaseLocation[];
    encounters: readonly (EncounterShellSource & { catalog?: Readonly<Record<string, Row>> })[];
    world_order: readonly string[];
    child_order: readonly string[];
    encounter_order: readonly string[];
    /** A missing/deleted parent is an explicit SELECT miss, not an unheld dependency. */
    missing_location_ids: readonly string[];
}
export interface FromBaseLocationMetadata { cdnUrl?: string; encounter?: FromBaseEncounterShellMetadata }

function validateLocationBase(base: FromBaseLocation): void {
    assertUncomputedFromBase(base);
    if (!fromBaseUuid(base.entity_id) || !Number.isSafeInteger(base.base_rev) || base.base_rev < 0
        || !fromBaseRecord(base.locations) || String(surfaceField({ locations: base.locations }, 'location_id')).toUpperCase() !== base.entity_id.toUpperCase()
        || !Array.isArray(base.location_map_markers)) throw new TypeError('Invalid raw location envelope');
}

/** Native surface only (build_location_arr allow_nesting=false). Hierarchy is a separate, named stage. */
export function compileLocationFromBase(base: FromBaseLocation, metadata: FromBaseLocationMetadata = {}) {
    validateLocationBase(base);
    const completedStages: string[] = [], missingStages: string[] = [...FROM_BASE_LOCATION_REMAINING_STAGES];
    const source = { locations: { ...base.locations, ...base.shell_context } };
    const bucket: Row = {};
    for (const name of columns) {
        const value = surfaceField(source, name) ?? null;
        if (['location_id', 'location_name', 'location_created_by_id'].includes(name)
            || value !== null && value !== '' && value !== 0 && value !== '0') bucket[name] = value;
    }
    const row: Row = { locations: bucket };
    completedStages.push('location-own-row-normalization');
    if (LOCATION_SHELL_CONTEXT_FIELDS.every(name => name in source.locations)) completedStages.push('location-joined-shell');
    else missingStages.push('location-shell-context');
    try {
        const pictures = surfacePictureRows(base.pictures, base.media_context);
        const own = compileSurfacePictures(pictures, base.entity_id, 'location_picture', metadata.cdnUrl);
        const maps = compileSurfacePictures(pictures, base.entity_id, 'location_map', metadata.cdnUrl);
        const parent = surfaceField(source, 'location_parent_id');
        const parentMaps = fromBaseUuid(parent) ? compileSurfacePictures(pictures, parent, 'location_map', metadata.cdnUrl) : [];
        const markers = compileMarkers(base);
        if (maps.length) for (const map of maps) if (markers.length) map.location_map_markers = fromBaseClone(markers);
        if (own.length || maps.length) row.pictures = { ...(own.length ? { location_picture: own } : {}), ...(maps.length ? { location_map: maps } : {}) };
        if (parentMaps.length) row.location_parent_map = parentMaps;
        completedStages.push('location-media-maps-markers');
    } catch (error) {
        if (error instanceof FromBaseStageMissing) missingStages.push(error.message); else throw error;
    }
    let hierarchyProjection: ReturnType<typeof compileLocationHierarchyFromBase> | undefined;
    if (base.hierarchy_context) {
        try {
            hierarchyProjection = compileLocationHierarchyFromBase(base, metadata);
            missingStages.splice(0, FROM_BASE_LOCATION_REMAINING_STAGES.length);
            completedStages.push(...hierarchyProjection.completedStages);
            missingStages.push(...hierarchyProjection.missingStages);
        } catch (error) {
            if (error instanceof FromBaseStageMissing) missingStages.push(error.message); else throw error;
        }
    }
    return { kind: missingStages.length ? 'missing-stage' as const : 'constructed-location' as const,
        row, hierarchyProjection, completedStages, missingStages, closedRefusalFamilies: [] };
}

/** Native attach_location_nesting, over raw depth-one surfaces and encounter shells.
 * Returns a separate presentation projection: no permissions or hierarchy enter the cached surface. */
export function compileLocationHierarchyFromBase(base: FromBaseLocation, metadata: FromBaseLocationMetadata = {},
    visitedIds: readonly string[] = [base.entity_id]) {
    validateLocationBase(base);
    const context = base.hierarchy_context;
    if (!context) throw new FromBaseStageMissing('location-hierarchy-input', base.entity_id);
    const visited = new Set(visitedIds.map(id => id.toUpperCase()));
    const source = { locations: { ...base.locations, ...base.shell_context } };
    const read = (name: string) => surfaceField(source, name);
    const locations = new Map<string, FromBaseLocation>();
    for (const location of context.locations) {
        if (!fromBaseUuid(location.entity_id) || locations.has(location.entity_id.toUpperCase())) throw new TypeError('Invalid hierarchy location identity');
        locations.set(location.entity_id.toUpperCase(), location);
    }
    const misses = new Set(context.missing_location_ids.map(id => {
        if (!fromBaseUuid(id) || locations.has(id.toUpperCase())) throw new TypeError('Invalid hierarchy location miss');
        return id.toUpperCase();
    }));
    const row: Row = {}, missingStages: string[] = [];
    const master = read('location_master_parent_id');
    const locationRead = (raw: FromBaseLocation, name: string) => surfaceField({ locations: raw.locations }, name);
    const same = (value: unknown, expected: unknown) => String(value ?? '').toUpperCase() === String(expected ?? '').toUpperCase();
    const surfaces = new Map<string, Row | null>();
    const surface = (id: string): Row | null => {
        const key = id.toUpperCase();
        if (visited.has(key) || misses.has(key)) return null;
        if (surfaces.has(key)) return surfaces.get(key)!;
        const raw = locations.get(key);
        if (!raw) throw new FromBaseStageMissing('location-hierarchy-surface-input', id);
        // Depth-one only: do not recursively attach the related location's own hierarchy.
        const result = compileLocationFromBase({ ...raw, hierarchy_context: undefined }, metadata);
        const gaps = result.missingStages.filter(stage => !FROM_BASE_LOCATION_REMAINING_STAGES.some(remaining => remaining === stage));
        missingStages.push(...gaps.map(stage => `location:${id}:${stage}`));
        const row = gaps.length ? null : result.row;
        surfaces.set(key, row);
        return row;
    };
    const ordered = (ids: readonly string[], accepts: (raw: FromBaseLocation) => boolean): Row[] => {
        const held = new Set(ids.map(id => id.toUpperCase()));
        if (held.size !== ids.length) throw new TypeError('Duplicate hierarchy location order');
        const rows: Row[] = [];
        for (const id of ids) {
            if (!fromBaseUuid(id)) throw new TypeError('Invalid hierarchy order id');
            if (visited.has(id.toUpperCase())) continue;
            const raw = locations.get(id.toUpperCase());
            if (!raw || !accepts(raw) || phpJsonInt(locationRead(raw, 'location_deleted')) !== 0)
                throw new TypeError('Invalid hierarchy location membership');
            const projected = surface(id); if (projected) rows.push(projected);
        }
        if ([...locations.values()].some(raw => !visited.has(raw.entity_id.toUpperCase()) && accepts(raw)
            && phpJsonInt(locationRead(raw, 'location_deleted')) === 0
            && !held.has(raw.entity_id.toUpperCase()))) throw new TypeError('Incomplete hierarchy location order');
        return rows;
    };
    if (phpJsonInt(read('location_world')) === 1) {
        const world = ordered(context.world_order, raw => same(locationRead(raw, 'location_master_parent_id'), base.entity_id));
        if (world.length) row.world_location = world;
    } else if (context.world_order.length) throw new TypeError('World order on non-world location');
    const parent = read('location_parent_id');
    if (phpJsonTruthy(parent)) {
        row.location_parent = [];
        if (fromBaseUuid(parent)) {
            const projected = surface(parent);
            if (projected && phpJsonInt(surfaceField(projected, 'location_deleted')) === 0) row.location_parent = [projected];
        }
    }
    const children = ordered(context.child_order, raw => same(locationRead(raw, 'location_parent_id'), base.entity_id)
        && (!phpJsonTruthy(master) || same(locationRead(raw, 'location_master_parent_id'), master)));
    if (children.length) row.location_child = children;
    const encounters = new Map(context.encounters.map(raw => [raw.entity_id.toUpperCase(), raw]));
    if (encounters.size !== context.encounters.length || new Set(context.encounter_order.map(id => id.toUpperCase())).size !== encounters.size
        || context.encounter_order.length !== encounters.size) throw new TypeError('Incomplete hierarchy encounter order');
    const shells: Row[] = [];
    for (const id of context.encounter_order) {
        const raw = encounters.get(id.toUpperCase());
        if (!raw || !fromBaseUuid(id) || !same(encounterField(raw, 'encounter_id'), id)
            || !same(encounterField(raw, 'encounter_location_id'), base.entity_id)
            || phpJsonInt(encounterField(raw, 'encounter_deleted')) !== 0
            || phpJsonTruthy(master) && !same(encounterField(raw, 'encounter_master_parent_id'), master))
            throw new TypeError('Invalid hierarchy encounter membership');
        const result = compileEncounterShellFromBase(raw, raw.catalog ?? base.catalog ?? {}, metadata.encounter);
        missingStages.push(...result.missingStages.map(stage => `encounter:${id}:${stage}`));
        // An unfinished status overlay must not look like a finished attached shell.
        if (!result.missingStages.length) shells.push(result.row);
    }
    if (shells.length) row.location_encounter = shells;
    return { row: Object.keys(row).length ? row : [] as [],
        completedStages: ['location-database-hierarchy-order', ...(!missingStages.length ? ['location-hierarchy-shells'] : [])], missingStages };
}

function compileMarkers(base: FromBaseLocation): Row[] {
    if (!base.location_map_markers.length) return [];
    if (!base.marker_order) throw new FromBaseStageMissing('location-marker-order', base.entity_id);
    const rows = new Map<string, Row>();
    for (const raw of base.location_map_markers) {
        const read = (key: string): unknown => surfaceField(raw, key) ?? null;
        const id = read('location_map_marker_id');
        if (!fromBaseUuid(id) || rows.has(id.toUpperCase()) || String(read('location_parent_id')).toUpperCase() !== base.entity_id.toUpperCase()
            || phpJsonInt(read('location_map_marker_deleted')) !== 0) throw new TypeError('Invalid location marker identity/membership');
        const row: Row = {};
        for (const name of ['location_map_marker_private', 'location_map_marker_deleted', 'location_map_marker_created_by_id',
            'location_map_marker_id', 'location_parent_id', 'location_map_marker_attached_to_type', 'location_map_marker_attached_to_id',
            'location_map_marker_type', 'location_map_marker_color']) row[name] = read(name);
        let coordinates = read('location_map_marker_coordinates');
        if (typeof coordinates === 'string') {
            try { const decoded: unknown = JSON.parse(coordinates); if (decoded !== null && typeof decoded === 'object') coordinates = decoded; }
            catch { /* Native keeps malformed source text. */ }
        }
        if (coordinates !== null && typeof coordinates === 'object') {
            coordinates = fromBaseClone(coordinates);
            for (const key of ['lat', 'lng']) if ((coordinates as Row)[key] != null) (coordinates as Row)[key] = phpJsonInt((coordinates as Row)[key]);
        }
        row.location_map_marker_coordinates = coordinates;
        for (const name of ['private', 'deleted']) if (phpJsonInt(row['location_map_marker_' + name]) === 0) delete row['location_map_marker_' + name];
        rows.set(id.toUpperCase(), row);
    }
    if (base.marker_order.length !== rows.size || new Set(base.marker_order.map(id => id.toUpperCase())).size !== rows.size)
        throw new TypeError('Incomplete location marker order');
    return base.marker_order.map(id => { const row = rows.get(id.toUpperCase()); if (!row) throw new TypeError('Unknown location marker order id'); return row; });
}
